import { useMemo, useState } from "react";
import { useResource, formatDate, titleCase, daysUntil } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { locationCreateFields, TASK_SUGGESTIONS } from "../lib/formOptions";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { Employee, LocationRow, SiteTask } from "../lib/types";

const PRIORITY = ["low", "medium", "high"].map((p) => ({ value: p, label: titleCase(p) }));
const STATUS = ["not_started", "in_progress", "completed", "cancelled"]
    .map((s) => ({ value: s, label: titleCase(s) }));

const priorityChip = (p: SiteTask["priority"]) => (p === "high" ? "block" : p === "medium" ? "warn" : "mute");
const statusChip = (s: SiteTask["status"]) =>
    s === "completed" ? "pass" : s === "in_progress" ? "info" : s === "cancelled" ? "mute" : "warn";

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; task: SiteTask }
    | { kind: "complete"; task: SiteTask }
    | { kind: "delete"; task: SiteTask }
    | null;

export const SiteTasks = () => {
    const { user } = useAuth();
    // Supervisors manage tasks at the locations they oversee. Row-level security
    // enforces the location part; this only decides whether to draw the button.
    const canManage = user?.role === "admin" || user?.role === "manager" || user?.role === "supervisor";

    const tasks = useResource<SiteTask[]>("/site-tasks");
    const locations = useResource<LocationRow[]>("/locations");
    const employees = useResource<Employee[]>("/employees");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<Dialog>(null);

    const refresh = () => { tasks.reload(); setDialog(null); };

    const nameOf = useMemo(() => {
        const l = new Map((locations.data ?? []).map((x) => [x.id, x.name]));
        const e = new Map((employees.data ?? []).map((x) => [x.id, x.full_name]));
        return {
            location: (id: string) => l.get(id) ?? "Unknown location",
            employee: (id: string | null) => (id ? e.get(id) ?? "Unknown" : "Unassigned"),
        };
    }, [locations.data, employees.data]);

    const participants = useResource<Array<{ id: string; full_name: string }>>("/participants");

    const fields: Field[] = [
        { name: "name", label: "Task", kind: "text", required: true,
          suggestions: TASK_SUGGESTIONS },
        { name: "description", label: "Description", kind: "textarea" },
        { name: "location_id", label: "Location", kind: "select", required: true,
          options: (locations.data ?? []).map((l) => ({ value: l.id, label: l.name })), half: true,
          addNew: { title: "New location", path: "/locations", fields: locationCreateFields() } },
        { name: "assigned_employee_id", label: "Assigned to", kind: "select", half: true,
          options: (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name })) },
        { name: "participant_id", label: "For participant (optional)", kind: "select", half: true,
          options: (participants.data ?? []).map((p) => ({ value: p.id, label: p.full_name })),
          help: "Ties the task to a provider's participant — their documents live on the participant's Documents tab." },
        { name: "priority", label: "Priority", kind: "select", options: PRIORITY, half: true },
        { name: "status", label: "Status", kind: "select", options: STATUS, half: true },
        { name: "start_date", label: "Start date", kind: "date", half: true },
        { name: "due_date", label: "Due date", kind: "date", half: true },
    ];

    const all = tasks.data ?? [];
    const filtered = all.filter((t) =>
        matches(search, t.name, t.description, nameOf.location(t.location_id)) &&
        (!filters.status || t.status === filters.status) &&
        (!filters.priority || t.priority === filters.priority) &&
        (!filters.location_id || t.location_id === filters.location_id)
    );

    if (tasks.error) return <div className="error-banner">{tasks.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <EntityForm title="Add Task" path="/site-tasks" fields={fields}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title={`Edit ${dialog.task.name}`} path="/site-tasks"
                    fields={fields} initial={dialog.task}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "complete" && (
                <EntityForm title={`Complete ${dialog.task.name}`} path="/site-tasks"
                    initial={{ ...dialog.task, status: "completed", completed_at: new Date().toISOString() }}
                    fields={[
                        { name: "status", label: "Status", kind: "select", options: STATUS, required: true },
                        { name: "completed_at", label: "Completed at", kind: "datetime", required: true },
                    ]}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "delete" && (
                <ConfirmDialog
                    title={`Delete "${dialog.task.name}"?`}
                    tone="danger"
                    message="The task is removed from the list."
                    warning="Unlike participant and workforce records, a site task carries no clinical history, so this is a genuine delete. If the task was done, mark it completed instead — that keeps the evidence that it happened."
                    confirmLabel="Delete task"
                    request={{ method: "delete", path: `/site-tasks/${dialog.task.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search tasks…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "status", allLabel: "All Status", options: STATUS },
                    { name: "priority", allLabel: "All Priority", options: PRIORITY },
                    { name: "location_id", allLabel: "All Locations",
                      options: (locations.data ?? []).map((l) => ({ value: l.id, label: l.name })) },
                ]}
                resultNote={filtered.length !== all.length ? `Showing ${filtered.length} of ${all.length} tasks` : undefined}
                action={canManage && (
                    <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Add Task</button>
                )}
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Task</th><th>Location</th><th>Assigned to</th>
                                    <th>Priority</th><th>Due</th><th>Status</th>
                                    {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((task) => {
                                    const overdue = task.due_date && daysUntil(task.due_date) < 0
                                        && task.status !== "completed" && task.status !== "cancelled";
                                    return (
                                        <tr key={task.id}>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{task.name}</div>
                                                {task.description && (
                                                    <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{task.description}</div>
                                                )}
                                            </td>
                                            <td>{nameOf.location(task.location_id)}</td>
                                            <td>
                                                {task.assigned_employee_id ? (
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                        <InitialsAvatar name={nameOf.employee(task.assigned_employee_id)} size={24} />
                                                        {nameOf.employee(task.assigned_employee_id)}
                                                    </div>
                                                ) : "—"}
                                            </td>
                                            <td><span className={`chip ${priorityChip(task.priority)}`}>{task.priority}</span></td>
                                            <td className="mono">
                                                {formatDate(task.due_date)}
                                                {overdue && <span className="chip block" style={{ marginLeft: 5 }}>overdue</span>}
                                            </td>
                                            <td><span className={`chip ${statusChip(task.status)}`}>{titleCase(task.status)}</span></td>
                                            {canManage && (
                                                <td>
                                                    <div className="row-actions">
                                                        <button className="icon-btn" onClick={() => setDialog({ kind: "edit", task })}>Edit</button>
                                                        {task.status !== "completed" && (
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "complete", task })}>Complete</button>
                                                        )}
                                                        <button className="icon-btn danger" onClick={() => setDialog({ kind: "delete", task })}>Delete</button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={canManage ? 7 : 6} className="empty">No tasks match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
